import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Loader2, CheckCircle2, MapPin } from "lucide-react";
import { SERVICES, APPS, EMAIL, WHATSAPP_DISPLAY, LOCATION, waLink } from "@/data/content";
import { Reveal, MaskLine, Eyebrow } from "./Reveal";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", company: "", service: "", message: "" };

const Field = ({ label, id, ...props }) => (
  <label className="block group">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--steel-2)] group-focus-within:text-[var(--cyan)] transition-colors">{label}</span>
    <input id={id} data-testid={`contact-input-${id}`} {...props} className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-white/30 outline-none focus:border-[var(--cyan)] transition-colors" />
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Mensagem enviada! Retornaremos em breve.");
    } catch (err) {
      toast.error("Não foi possível enviar. Verifique os dados ou fale pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" data-testid="contact-section" className="relative bg-[var(--navy)] py-24 md:py-36 overflow-hidden">
      <div className="absolute inset-0 blueprint opacity-40" />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow testid="contact-eyebrow">Contato · Orçamento</Eyebrow></Reveal>
          <h2 className="mt-6 font-display font-black uppercase text-5xl md:text-7xl leading-[0.9]">
            <MaskLine inView>Vamos tirar</MaskLine>
            <MaskLine inView delay={0.1}><span className="steel-text">seu projeto</span></MaskLine>
            <MaskLine inView delay={0.2}><span className="text-[var(--blue)]">do papel.</span></MaskLine>
          </h2>
          <Reveal delay={0.2}>
            <div className="mt-12 space-y-4">
              <a data-testid="contact-email-link" href={`mailto:${EMAIL}`} className="group flex items-center justify-between border-t border-white/10 pt-5 text-white">
                <span className="flex items-center gap-4"><Mail size={20} className="text-[var(--cyan)]" /><span className="text-base md:text-lg break-all">{EMAIL}</span></span>
                <ArrowUpRight size={20} className="transition-transform group-hover:rotate-45" />
              </a>
              <a data-testid="contact-whatsapp-link" href={waLink("Olá! Gostaria de falar com a FMACIEL.")} target="_blank" rel="noreferrer" className="group flex items-center justify-between border-y border-white/10 py-5 text-white">
                <span className="flex items-center gap-4"><WaIcon /><span className="text-base md:text-lg">{WHATSAPP_DISPLAY}</span></span>
                <ArrowUpRight size={20} className="transition-transform group-hover:rotate-45" />
              </a>
              <p data-testid="contact-location" className="flex items-center gap-4 border-b border-white/10 pb-5 text-white">
                <MapPin size={20} className="text-[var(--cyan)]" /><span className="text-base md:text-lg">{LOCATION}</span>
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="relative rounded-sm border border-white/10 bg-[var(--ink)]/70 backdrop-blur-xl p-6 sm:p-10">
            <span className="crosshair -left-[7px] -top-[7px]" /><span className="crosshair -right-[7px] -bottom-[7px]" />
            <div className="grid sm:grid-cols-2 gap-8">
              <Field label="Nome *" id="name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Seu nome" />
              <Field label="E-mail *" id="email" type="email" required value={form.email} onChange={set("email")} placeholder="voce@empresa.com" />
              <Field label="Telefone" id="phone" value={form.phone} onChange={set("phone")} placeholder="(00) 00000-0000" />
              <Field label="Empresa" id="company" value={form.company} onChange={set("company")} placeholder="Nome da empresa" />
            </div>
            <div className="block mt-8">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--steel-2)]">Interesse</span>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="contact-select-service" className="mt-2 h-auto rounded-none border-0 border-b border-white/20 bg-transparent px-0 py-3 text-base text-white shadow-none focus:ring-0 focus:border-[var(--cyan)] data-[placeholder]:text-white/40">
                  <SelectValue placeholder="Selecione um serviço ou aplicativo" />
                </SelectTrigger>
                <SelectContent data-lenis-prevent className="max-h-[320px] border-white/15 bg-[#0B132B] text-[#E2E8F0]">
                  <SelectGroup>
                    <SelectLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--cyan)]">Serviços</SelectLabel>
                    {SERVICES.map((s) => <SelectItem key={s.id} data-testid={`service-option-${s.id}`} value={s.title} className="text-[#E2E8F0] focus:bg-[var(--blue)] focus:text-white">{s.title}</SelectItem>)}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--cyan)]">Aplicativos</SelectLabel>
                    {APPS.map((a) => <SelectItem key={a.id} data-testid={`app-option-${a.id}`} value={`App: ${a.full}`} className="text-[#E2E8F0] focus:bg-[var(--blue)] focus:text-white">{a.full}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <label className="block mt-8 group">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--steel-2)] group-focus-within:text-[var(--cyan)]">Mensagem *</span>
              <textarea data-testid="contact-input-message" required minLength={5} rows={4} value={form.message} onChange={set("message")} placeholder="Conte um pouco sobre o seu projeto" className="mt-2 w-full resize-none bg-transparent border-b border-white/20 py-3 text-white placeholder:text-white/30 outline-none focus:border-[var(--cyan)]" />
            </label>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <motion.button whileTap={{ scale: 0.97 }} data-testid="contact-submit-button" type="submit" disabled={loading} className="inline-flex items-center gap-3 rounded-full bg-[var(--blue)] px-8 py-4 font-medium text-white hover:bg-[#4a7dff] disabled:opacity-60 transition-colors">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpRight size={18} />} Enviar mensagem
              </motion.button>
              {sent && (
                <motion.span initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} data-testid="contact-success" className="flex items-center gap-2 text-sm text-[var(--cyan)]">
                  <CheckCircle2 size={18} /> Recebemos sua mensagem.
                </motion.span>
              )}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export const WaIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#25D366" aria-hidden>
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5 2.5 1 3 .8 3.6.7.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 21.8c-1.8 0-3.5-.5-5-1.4l-.4-.2-3.7 1 1-3.6-.2-.4C2.7 15.6 2.2 13.8 2.2 12 2.2 6.6 6.6 2.2 12 2.2S21.8 6.6 21.8 12 17.4 21.8 12 21.8zM12 0C5.4 0 0 5.4 0 12c0 2.1.6 4.2 1.6 6L0 24l6.2-1.6c1.8 1 3.8 1.5 5.8 1.5 6.6 0 12-5.4 12-12S18.6 0 12 0z" />
  </svg>
);
